/**
 * ChatInput — Message composer for Maestro terminal chat
 * MAESTRO:P0D
 *
 * - Textarea expands up to ~4 lines then scrolls
 * - Enter sends; Shift+Enter inserts a newline
 * - Character counter at 80% of max (2000)
 * - Orange accent border on focus (matching brand primary)
 * - Disabled state while loading (shows pulsing indicator)
 * - 48px min touch targets on send button
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';

const MAX_CHARS = 2000;
const WARN_THRESHOLD = Math.floor(MAX_CHARS * 0.8); // show counter at 80%

/**
 * @param {Object} props
 * @param {(text: string) => void} props.onSend - Callback with trimmed message text
 * @param {boolean} [props.disabled] - Disable input and button while loading
 * @param {string} [props.placeholder]
 */
export default function ChatInput({ onSend, disabled = false, placeholder = 'Ask Maestro anything about your collection…' }) {
  const [value, setValue] = useState('');
  const textareaRef = useRef(null);

  // Auto-grow textarea up to max-height via scrollHeight
  useEffect(() => {
    const el = textareaRef.current;
    /* v8 ignore next */
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [value]);

  // Focus input on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    // Reset height after clear
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const charsLeft = MAX_CHARS - value.length;
  const showCounter = value.length >= WARN_THRESHOLD;
  const isOverLimit = value.length > MAX_CHARS;
  const canSend = value.trim().length > 0 && !disabled && !isOverLimit;

  return (
    <div className="px-3 pb-3 pt-2 bg-zinc-950 border-t border-zinc-800">
      <div
        className={`
          flex items-end gap-2 bg-zinc-900 rounded-xl border transition-colors duration-150 px-3 py-2
          ${disabled ? 'border-zinc-800 opacity-70' : 'border-zinc-700 focus-within:border-orange-500'}
        `}
      >
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          aria-label="Message Maestro"
          aria-multiline="true"
          aria-describedby={showCounter ? 'char-counter' : undefined}
          maxLength={MAX_CHARS + 1} // let user type slightly over for UX; we block send
          rows={1}
          className={`
            flex-1 bg-transparent text-zinc-100 text-sm placeholder-zinc-600 resize-none
            focus:outline-none font-mono leading-relaxed min-h-[24px]
            disabled:cursor-not-allowed
          `}
          style={{ maxHeight: '120px' }}
        />

        {/* Send button — 48×48 touch target */}
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
          className={`
            flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 focus:ring-offset-zinc-900
            min-h-[48px] min-w-[48px] sm:min-h-[36px] sm:min-w-[36px] sm:w-9 sm:h-9
            ${canSend
              ? 'bg-orange-500 hover:bg-orange-600 text-white active:scale-95'
              : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
            }
          `}
        >
          {disabled ? (
            <span className="w-4 h-4 rounded-full border-2 border-zinc-500 border-t-transparent animate-spin" aria-hidden="true" />
          ) : (
            <ArrowUp size={16} aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Character counter + hint */}
      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[10px] text-zinc-700 font-mono select-none hidden sm:block">
          Enter to send · Shift+Enter for newline
        </span>
        {showCounter && (
          <span
            id="char-counter"
            aria-live="polite"
            className={`text-[10px] font-mono ml-auto ${isOverLimit ? 'text-red-400' : 'text-zinc-500'}`}
          >
            {charsLeft < 0 ? `${Math.abs(charsLeft)} over limit` : `${charsLeft} left`}
          </span>
        )}
      </div>
    </div>
  );
}
